import {
  type ReactElement,
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useId,
  useRef,
  useState,
} from 'react';

type FilterPanelContextValue = {
  defaultOpen: boolean;
  defaultFilters: Array<{ id: string; value: unknown }>;
  openPreference: boolean | undefined;
  setOpenPreference: (isOpen: boolean) => void;
  pinnedOpen: boolean;
  pinOpen: () => void;
  clearPin: () => void;
  onToggle: () => void;
  panelId: string;
  triggerRef: RefObject<HTMLButtonElement>;
};

const FilterPanelContext = createContext<FilterPanelContextValue | null>(null);

export const FilterPanelProvider = ({
  children,
  defaultOpen = false,
  storageKey,
  defaultFilters,
  openPreference,
  onOpenPreferenceChange,
  onToggle,
}: {
  children: ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  defaultFilters: Array<{ id: string; value: unknown }>;
  openPreference: boolean | undefined;
  onOpenPreferenceChange: (isOpen: boolean) => void;
  onToggle: () => void;
}): ReactElement => {
  const [pinnedState, setPinnedState] = useState<{
    storageKey: string | undefined;
    isOpen: boolean;
  }>({ storageKey, isOpen: false });
  if (pinnedState.storageKey !== storageKey) {
    setPinnedState({ storageKey, isOpen: false });
  }
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <FilterPanelContext.Provider
      value={{
        defaultOpen,
        defaultFilters,
        openPreference,
        setOpenPreference: onOpenPreferenceChange,
        pinnedOpen: pinnedState.storageKey === storageKey && pinnedState.isOpen,
        pinOpen: () => setPinnedState({ storageKey, isOpen: true }),
        clearPin: () => setPinnedState({ storageKey, isOpen: false }),
        onToggle,
        panelId,
        triggerRef,
      }}
    >
      {children}
    </FilterPanelContext.Provider>
  );
};

export const useFilterPanelContext = (): FilterPanelContextValue => {
  const context = useContext(FilterPanelContext);
  if (!context) throw new Error('Filter panels require a FiltersProvider');
  return context;
};
